import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { BetaFeedbackButton } from "./BetaFeedbackButton";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "./ui/dropdown-menu";
import { LayoutDashboard, GraduationCap, MessageSquareText, Trophy, LogOut, Plane, Sparkles, Receipt, Users, UserCog, User, Radar, ChevronDown } from "lucide-react";
import { PRICING_ENABLED } from "../config";
import { useSiteStatus } from "../lib/siteStatus";

const allLinks = [
  { to: "/dashboard", label: "Academies", icon: GraduationCap, id: "nav-dashboard" },
  { to: "/instructor", label: "AI Instructor", icon: MessageSquareText, id: "nav-instructor" },
  { to: "/achievements", label: "Achievements", icon: Trophy, id: "nav-achievements" },
  { to: "/profile", label: "Profile", icon: User, id: "nav-profile" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const { maintenance } = useSiteStatus();
  const links = maintenance ? allLinks.filter((l) => l.to !== "/instructor") : allLinks;

  return (
    <header className="sticky top-0 z-50 glass border-b border-border/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <div className="h-9 w-9 rounded-sm bg-primary flex items-center justify-center glow-cyan">
            <Plane className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-lg tracking-tight">
            ANSWERSTRIKE <span className="text-primary">ACADEMY</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => {
            const active = loc.pathname.startsWith(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-sm text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
              >
                <l.icon className="h-4 w-4" />
                {l.label}
              </Link>
            );
          })}
          {user?.role === "admin" && (
            <Link
              to="/admin/command-center"
              data-testid="nav-command-center"
              className={`flex items-center gap-2 px-3.5 py-2 rounded-sm text-sm font-medium transition-colors ${
                loc.pathname.startsWith("/admin/command-center")
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
            >
              <Radar className="h-4 w-4" />
              Command
            </Link>
          )}
          {user?.role === "admin" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-admin-menu"
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-sm text-sm font-medium transition-colors outline-none ${
                    (loc.pathname === "/admin" || loc.pathname.startsWith("/admin/leads") || loc.pathname.startsWith("/admin/users"))
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
                >
                  <UserCog className="h-4 w-4" /> Admin <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem asChild data-testid="nav-admin-reactivation">
                  <Link to="/admin/reactivation" className="flex items-center gap-2 cursor-pointer">
                    <Radar className="h-4 w-4" /> Reactivation
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin">
                  <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                    <Receipt className="h-4 w-4" /> Transactions
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin-leads">
                  <Link to="/admin/leads" className="flex items-center gap-2 cursor-pointer">
                    <Users className="h-4 w-4" /> Leads
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="nav-admin-users">
                  <Link to="/admin/users" className="flex items-center gap-2 cursor-pointer">
                    <UserCog className="h-4 w-4" /> Users
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block"><BetaFeedbackButton /></div>
          {PRICING_ENABLED && !maintenance && user && !user.has_paid && (
            <Link to="/checkout" data-testid="nav-upgrade">
              <Button size="sm" className="hidden sm:flex bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm font-semibold">
                <Sparkles className="h-4 w-4 mr-1.5" /> Unlock Full Course
              </Button>
            </Link>
          )}
          <div className="hidden sm:flex flex-col items-end leading-tight">
            <span className="text-sm font-medium">{user?.name}</span>
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
              {user?.role === "admin" ? "Commander" : "Cadet"}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            data-testid="nav-logout"
            onClick={() => {
              logout();
              nav("/login");
            }}
            className="text-muted-foreground hover:text-destructive"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};
